import React, { ReactElement, useContext, useState } from 'react';
import { uniq } from 'lodash';
import { Button, Form, Modal, message, Typography } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileUpload } from '@fortawesome/free-solid-svg-icons';
import TextArea from 'antd/lib/input/TextArea';

import { searchHeaderIconGrey } from 'common/constants/colors';
import { simpleFetch } from 'common/utils/http';
import { AttributesContext, SearchPaneResultsContext } from 'common/contexts';
import AttributePicker from 'common/components/AttributePicker';
import { PersonUserData } from 'common/constants/types';
import { EditUserContext, SelectedUsersContext } from 'pages/UserPage/context';
import { orderSearchResults } from '../../searchUtils';

const { Text } = Typography;

const fetchPeopleByAttributes = (attr: string, attrVal: string) => {
  const searchParams = new URLSearchParams();
  searchParams.set(attr, attrVal);

  return simpleFetch(`/api/v1/list?${searchParams.toString()}`);
};

const validateSelectPeopleReq = (attributVal: string[], attribute?: string) => {
  if (!attribute) {
    message.error('You must specify an attribute');
    return false;
  }
  if (attributVal.length === 0) {
    message.error('You must specify at least one attribute value');
    return false;
  }

  return true;
};

const showNoOrMoreUsersFound = (noUsersAttrVals: string[], moreUsersAttrVals: string[]) => {
  Modal.info({
    title: 'Some attribute values have unexpected results',
    content: (
      <div>
        {noUsersAttrVals.length > 0 && (
          <>
            <div>Attribute values with no users</div>
            <ul>
              {noUsersAttrVals.map(attrVal => (
                <li key={attrVal}>{attrVal}</li>
              ))}
            </ul>
          </>
        )}

        {moreUsersAttrVals.length > 0 && (
          <>
            <div>Attribute values with more than one users. All of whom are included in the results:</div>
            <ul>
              {moreUsersAttrVals.map(attrVal => (
                <li key={attrVal}>{attrVal}</li>
              ))}
            </ul>
          </>
        )}
      </div>
    ),
  });
};

function MountModalButton({ onClick }: { onClick: () => void }): ReactElement {
  return (
    <div onClick={onClick} role="button" style={{ cursor: 'pointer' }}>
      <FontAwesomeIcon icon={faFileUpload} style={{ color: searchHeaderIconGrey }} />
    </div>
  );
}

function SelectPeopleByAttribute() {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [attribute, setAttribute] = useState<string | undefined>(undefined);
  const [attributeValArray, setAttributeValArray] = useState<string[]>([]);
  const { setSortedSearchResults } = useContext(SearchPaneResultsContext);
  const { setEditUser } = useContext(EditUserContext);
  const { setSelectedUsers } = useContext(SelectedUsersContext);
  const { attributes } = useContext(AttributesContext);

  const updateAttrValue = (attrVals: string) => {
    setAttributeValArray(attrVals.split('\n').filter(val => val.length !== 0));
  };

  const closeModal = () => {
    setAttribute(undefined);
    setAttributeValArray([]);
    setModalOpen(false);
  };

  const selectUsersByAttrVal = async () => {
    if (!validateSelectPeopleReq(attributeValArray, attribute)) return;

    const selectedUsers: PersonUserData[] = [];
    const noUserFound: string[] = [];
    const moreUsersFound: string[] = [];
    await Promise.all(
      uniq(attributeValArray).map(async attrVal => {
        let people: PersonUserData[] = [];
        try {
          people = (await fetchPeopleByAttributes(attribute || '', attrVal)) || [];
        } catch (e) {
          message.error(JSON.stringify(e));
        }

        if (people.length === 0) {
          noUserFound.push(attrVal);
        } else if (people.length > 1) {
          selectedUsers.push(...people);
          moreUsersFound.push(attrVal);
        } else {
          selectedUsers.push(...people);
        }
      })
    );

    if (selectedUsers.length > 0) {
      setSortedSearchResults(orderSearchResults(selectedUsers));
      setEditUser(false);
      setSelectedUsers([selectedUsers[0].person_id]);
    }
    closeModal();

    if (noUserFound.length > 0 || moreUsersFound.length > 0) {
      showNoOrMoreUsersFound(noUserFound, moreUsersFound);
    }
  };

  return (
    <>
      <MountModalButton onClick={() => setModalOpen(true)} />
      {modalOpen && (
        <Modal
          footer={[
            <Button key="cancel" onClick={closeModal} type="default">
              Cancel
            </Button>,
            <Button key="select" onClick={selectUsersByAttrVal} type="primary">
              Select
            </Button>,
          ]}
          onCancel={closeModal}
          open={modalOpen}
          title="Select People By Attribute Value"
        >
          <Form layout="vertical">
            <Form.Item label="Which Attribute">
              {attribute ? (
                <div>
                  <Text strong>{attribute}</Text>
                  <Button onClick={() => setAttribute(undefined)} type="link">
                    X
                  </Button>
                </div>
              ) : (
                <AttributePicker
                  excludeAttributes
                  showUneditable
                  showUnlistable
                  attributes={attributes}
                  onChange={([, , attributeSelected]) => {
                    setAttribute(`${attributeSelected}`);
                  }}
                />
              )}
            </Form.Item>
            <Form.Item label="Attribute Values (for multiple values, separate them by a newline)">
              <TextArea onChange={e => updateAttrValue(e.target.value)} rows={5} />
            </Form.Item>
          </Form>
        </Modal>
      )}
    </>
  );
}
export default SelectPeopleByAttribute;
